import type {Action} from './serverProtocolTypes'
import _ from 'lodash'
import type {Namespace, Namespaces} from '../../types'
import {
    ADDITIONAL_SAVED_NAMESPACES,
    DATA_TYPES,
    MULTILINGUAL_TYPES,
    PARTIALLY_SAVED_NAMESPACES,
    VIEW_MODES,
    INFRA_NAMESPACES
} from '../../constants/constants'
import {PAGE_FRAGMENT_TYPE} from '@wix/document-manager-core'

type NamespacesOp = (data: Namespaces) => Namespaces

const arraysHaveItemsInCommon = <T>(a: readonly T[], b: readonly T[]): boolean => _.intersection(a, b).length > 0

/** These namespaces should be included in csave transactions in their entirety */
const COMPLETELY_ALLOWED_NAMESPACES: readonly string[] = _.flatMap(
    [DATA_TYPES, VIEW_MODES, MULTILINGUAL_TYPES],
    _.values
).concat(ADDITIONAL_SAVED_NAMESPACES)

if (arraysHaveItemsInCommon(_.keys(PARTIALLY_SAVED_NAMESPACES), COMPLETELY_ALLOWED_NAMESPACES)) {
    throw new Error(
        'PARTIALLY_SAVED_NAMESPACES and COMPLETELY_ALLOWED_NAMESPACES should not contain namespaces in common'
    )
}

const NAMESPACES_TO_KEEP: readonly string[] = [...COMPLETELY_ALLOWED_NAMESPACES, ..._.keys(PARTIALLY_SAVED_NAMESPACES)]

const removeExcludedKeysFromNamespace = (data: Namespace, name: string): Namespace => {
    const allowedKeys = PARTIALLY_SAVED_NAMESPACES[name]
    if (allowedKeys) {
        return _.pick(data, allowedKeys)
    }
    return data
}

const removeExcludedNamespaces: NamespacesOp = data => _.pick(data, NAMESPACES_TO_KEEP)

const removeExcludedKeysFromNamespaces: NamespacesOp = data => _.mapValues(data, removeExcludedKeysFromNamespace)

const removeExclusions: NamespacesOp = _.flow([removeExcludedNamespaces, removeExcludedKeysFromNamespaces])

const removeInfraExclusions: NamespacesOp = (data: Namespaces) => {
    return _.omit(data, _.values(INFRA_NAMESPACES))
}

const allowDiff = ({namespace, id, fragmentType}: Action): boolean => {
    const partial = PARTIALLY_SAVED_NAMESPACES[namespace!]
    const allowedByPartial = () => partial && _.includes(partial, id)
    const allowedByComplete = () => _.includes(COMPLETELY_ALLOWED_NAMESPACES, namespace)
    const allowedByFragmentType = fragmentType && fragmentType !== PAGE_FRAGMENT_TYPE
    return allowedByFragmentType || allowedByPartial() || allowedByComplete()
}

const removeExclusionsFromDiffs = (diffs: Action[]): Action[] => _.filter(diffs, allowDiff)

const removeTransientData = (diffs: Action[]): Action[] =>
    _.reject(diffs, {namespace: 'documentServicesModel', id: 'versionInfo'})

const removeFromLoadStore: (diffs: Action[]) => Action[] = _.flow([removeTransientData, removeExclusionsFromDiffs])

export {removeFromLoadStore, removeExclusionsFromDiffs, removeExclusions, removeInfraExclusions}
